import { browser } from "$app/environment";

import ky from "ky";
import { derived, get } from "svelte/store";

import { trackEvent } from "$lib/core/posthog";
import useSWR from "$lib/core/swr";
import {
  isTimeFormat,
  type TimeFormat,
  timeFormatPreference,
} from "$lib/core/time-format";
import {
  isThemePreference,
  themePreference,
  type ThemePreference,
} from "$lib/design-systems/product/foundations/theme";
import {
  makeErrorToast,
  makeToast,
} from "$lib/design-systems/product/primitives/feedback/toast";
import type { IdentityProviderType } from "~proto/modal_server/proto/public_api";

export type User =
  | { loggedIn: false }
  | {
      loggedIn: true;
      userId: string;
      username: string;
      isAdmin: boolean;
      notificationEmail: string;
      approved: boolean;
      name: string;
      avatarUrl: string;
      timezone: string | null;
      funnelEvents: string[];
      lastUsedWorkspace: string;
      identityProviderTypes: IdentityProviderType[];
      onboardingUseCases: string;
      /** The account-level theme preference; null when the user has not set one. */
      themePreference: ThemePreference | null;
      /** The account-level time format; null when the user has not set one. */
      timeFormatPreference: TimeFormat | null;
    };

const { data, isValidating, mutate } = useSWR<User>("/api/user", {
  initialData: () => {
    if (browser) {
      try {
        const serialized = localStorage.getItem("modal-user");
        const savedUser: User = serialized ? JSON.parse(serialized) : undefined;
        if (savedUser && Object.hasOwn(savedUser, "loggedIn")) {
          return savedUser;
        } else {
          return undefined;
        }
      } catch (e) {
        // A SecurityError is thrown if the user has "block third party cookies & site data" enabled.
        console.warn("Failed to parse user from localStorage", e);
        return undefined;
      }
    }
  },
  ignoreDestroy: true,
  refreshInterval: 60000,
});

if (browser) {
  data.subscribe((user) => {
    if (user) {
      localStorage.setItem("modal-user", JSON.stringify(user));

      // @TODO @ro remove this once we've confirmed new login changes.
      if (user.loggedIn && user.isAdmin) {
        localStorage.setItem("useLoginV2", "true");
      }

      // Store the last-login-method to help users remember how they last logged in on their browser.
      // We setItem here to only save after a successful login.
      //
      // TODO @ro remove this once we've confirmed new login changes.
      if (user.loggedIn && user.identityProviderTypes?.length > 0) {
        localStorage.setItem(
          "last-login-method",
          user.identityProviderTypes[0],
        );
      }

      // Adopt the account's theme preference so it follows the user across
      // browsers. `themePreference.set` also writes the cookie, keeping SSR
      // and unauthenticated surfaces consistent with the account setting.
      if (
        user.loggedIn &&
        isThemePreference(user.themePreference) &&
        user.themePreference !== get(themePreference)
      ) {
        themePreference.set(user.themePreference);
      }

      // Same for the clock, so a 12-hour reader gets it on every browser.
      if (
        user.loggedIn &&
        isTimeFormat(user.timeFormatPreference) &&
        user.timeFormatPreference !== get(timeFormatPreference)
      ) {
        timeFormatPreference.set(user.timeFormatPreference);
      }
    }
  });
}

/** The UI surface a theme preference change originated from. */
export type ThemePreferenceSource = "user_menu" | "settings_profile";

/**
 * Applies a theme preference immediately and persists it to the logged-in
 * user's account so it follows them across browsers. The cookie is always
 * written (via `themePreference.set`) so SSR and unauthenticated surfaces
 * keep a browser-level setting.
 */
export async function setThemePreference(
  value: ThemePreference,
  source: ThemePreferenceSource,
) {
  const previous = get(themePreference);
  themePreference.set(value);
  if (value !== previous) {
    trackEvent("theme_preference_changed", {
      namespace: "settings",
      theme_preference: value,
      previous_theme_preference: previous,
      source,
    });
  }
  const currentUser = get(data);
  if (!currentUser?.loggedIn || currentUser.themePreference === value) return;
  // Optimistically update the cached user so a concurrent revalidation does
  // not briefly revert the preference while the save is in flight.
  mutate({ ...currentUser, themePreference: value }, false);
  try {
    await ky.patch("/api/user/settings", {
      json: { theme_preference: value },
    });
  } catch (e: any) {
    makeErrorToast(e);
  }
}

/**
 * Applies a time format immediately and persists it to the logged-in user's
 * account so it follows them across browsers. The cookie is always written
 * (via `timeFormatPreference.set`) so the next load starts on the right clock.
 */
export async function setTimeFormatPreference(value: TimeFormat) {
  const previous = get(timeFormatPreference);
  timeFormatPreference.set(value);
  if (value !== previous) {
    trackEvent("time_format_preference_changed", {
      namespace: "settings",
      time_format_preference: value,
      previous_time_format_preference: previous,
    });
  }
  const currentUser = get(data);
  if (!currentUser?.loggedIn || currentUser.timeFormatPreference === value)
    return;
  // Optimistically update the cached user so a concurrent revalidation does
  // not briefly revert the preference while the save is in flight.
  mutate({ ...currentUser, timeFormatPreference: value }, false);
  try {
    await ky.patch("/api/user/settings", {
      json: { time_format_preference: value },
    });
    makeToast({
      kind: "success",
      message: "Time format preference updated!",
    });
  } catch (e: any) {
    makeErrorToast(e);
  }
}

/** Thrown when GET /api/user returns a non-2xx response. */
export class UserFetchError extends Error {
  status: number;

  constructor(status: number) {
    super(`Failed to fetch user: ${status}`);
    this.name = "UserFetchError";
    this.status = status;
  }
}

/** Inside a load() function, retrieve the current logged-in user. */
export async function getUserIsomorphic(
  fetch: typeof globalThis.fetch,
): Promise<User> {
  const cachedUser = get(data);
  if (cachedUser && cachedUser.loggedIn) {
    // Client navigation, and user is already logged in.
    return cachedUser;
  } else {
    const res = await fetch("/api/user");
    if (!res.ok) {
      throw new UserFetchError(res.status);
    }
    return await res.json();
  }
}

/** Optimistically update cached last-used workspace so subsequent reads reflect it immediately. */
export function setLastUsedWorkspace(workspace: string) {
  const currentUser = get(data);
  if (currentUser?.loggedIn && currentUser.lastUsedWorkspace !== workspace) {
    mutate({ ...currentUser, lastUsedWorkspace: workspace }, false);
  }
}

/**
 * Remember the workspace the user is viewing so `/apps` and friends land there
 * next time. Best effort: callers fire and forget, and a failed save (the
 * workspace is gone, the request timed out) only affects that default.
 */
export function saveLastUsedWorkspace(workspace: string): Promise<void> {
  setLastUsedWorkspace(workspace);
  return postBestEffort("/api/user/last-used-workspace", { workspace });
}

/** Remember the user's environment in a workspace; best effort, like {@link saveLastUsedWorkspace}. */
export function saveLastUsedEnvironment(
  workspace: string,
  environment: string,
): Promise<void> {
  return postBestEffort(`/api/user/last-used-environment/${workspace}`, {
    environment,
  });
}

async function postBestEffort(url: string, json: unknown): Promise<void> {
  try {
    await ky.post(url, { json });
  } catch {
    // Nothing reads the response, and the next page view saves it again.
  }
}

/**
 * The account's configured timezone, or undefined to mean the browser's.
 * Resolves after the first render, so anything displaying a time should
 * subscribe rather than read it once.
 */
export const userTimezone = derived(
  data,
  (u) => (u?.loggedIn && u.timezone) || undefined,
);

export { data as user, isValidating, mutate };
